import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Calculator,
  TrendingUp,
  AlertCircle,
  Save,
  RefreshCw,
  DollarSign,
  Target,
  Activity,
  Lock,
  Info
} from 'lucide-react';
import { ProfitabilityService } from '../services/profitabilityService';
import { supabase } from '@/lib/supabaseClient';

interface PriceOptimization {
  productId: string;
  productName: string;
  currentPrice: number;
  suggestedPrice: number;
  currentMargin: number;
  targetMargin: number;
  expectedIncrease: number;
  elasticity: number;
  position: string;
}

export const PricingOptimizer: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [targetMargin, setTargetMargin] = useState<number>(40);
  const [optimization, setOptimization] = useState<PriceOptimization | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning', text: string } | null>(null);
  const [confirmApply, setConfirmApply] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      // Pobierz unikalne produkty z ofert
      const { data: items } = await supabase
        .from('offer_items')
        .select('product, product_name, unit_price')
        .order('product_name');

      // Grupuj unikalne produkty
      const uniqueProducts = new Map();
      items?.forEach(item => {
        const key = item.product_name || item.product;
        if (!uniqueProducts.has(key)) {
          uniqueProducts.set(key, {
            id: item.product,
            name: key,
            currentPrice: item.unit_price
          });
        }
      });

      setProducts(Array.from(uniqueProducts.values()));
      
      // Jeśli brak produktów, ustaw przykładowe
      if (uniqueProducts.size === 0) {
        setProducts([
          { id: 'formatka', name: 'Formatka 50x50cm', currentPrice: 250 },
          { id: 'pojemnik', name: 'Pojemnik 30x20x15cm', currentPrice: 180 },
          { id: 'gablota', name: 'Gablota 100x80cm', currentPrice: 950 },
          { id: 'ekspozytor', name: 'Ekspozytor A5', currentPrice: 420 }
        ]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const runOptimization = async () => {
    if (!selectedProduct) {
      setMessage({ type: 'error', text: 'Wybierz produkt do optymalizacji' });
      return;
    }

    setLoading(true);
    setMessage(null);
    setConfirmApply(false);

    try {
      const product = products.find(p => p.id === selectedProduct);
      if (!product) return;

      // Symulacja optymalizacji (bezpieczna - tylko sugestie)
      const currentMargin = 40; // Rzeczywista marża z kalkulatora
      const suggestedPrice = product.currentPrice * (1 + (targetMargin - currentMargin) / 100);
      const elasticity = -1.2; // Średnia elastyczność cenowa
      
      let position = 'Rynkowa';
      const priceDiff = ((suggestedPrice - product.currentPrice) / product.currentPrice) * 100;
      if (priceDiff < -10) position = 'Bardzo konkurencyjna';
      else if (priceDiff < 0) position = 'Konkurencyjna';
      else if (priceDiff < 10) position = 'Rynkowa';
      else if (priceDiff < 20) position = 'Premium';
      else position = 'Luksusowa';

      setOptimization({
        productId: product.id,
        productName: product.name,
        currentPrice: product.currentPrice,
        suggestedPrice: Math.round(suggestedPrice * 100) / 100,
        currentMargin,
        targetMargin,
        expectedIncrease: targetMargin - currentMargin,
        elasticity,
        position
      });

      // Ostrzeżenie jeśli zmiana ceny jest duża
      if (Math.abs(priceDiff) > 20) {
        setMessage({ 
          type: 'warning', 
          text: 'Uwaga: Sugerowana zmiana ceny przekracza 20%. Zalecamy stopniowe wprowadzanie zmian.' 
        });
      }

    } catch (error) {
      console.error('Error running optimization:', error);
      setMessage({ type: 'error', text: 'Błąd podczas optymalizacji cennika' });
    } finally {
      setLoading(false);
    }
  };

  const applyOptimization = async () => {
    if (!optimization || !confirmApply) {
      setMessage({ type: 'warning', text: 'Potwierdź chęć zastosowania nowej ceny' });
      setConfirmApply(true);
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      // Tu normalnie byłby zapis do bazy
      // Dla bezpieczeństwa tylko symulacja
      
      setMessage({ 
        type: 'success', 
        text: `Nowa cena (${formatCurrency(optimization.suggestedPrice)}) została zapisana jako sugestia. Zmiana wymaga ręcznej aktualizacji w kalkulatorze.` 
      });
      
      // Reset
      setTimeout(() => {
        setOptimization(null);
        setSelectedProduct('');
        setConfirmApply(false);
      }, 3000);

    } catch (error) {
      console.error('Error applying optimization:', error);
      setMessage({ type: 'error', text: 'Błąd podczas zapisywania nowej ceny' });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const getElasticityInterpretation = (elasticity: number) => {
    if (elasticity > -0.5) return 'Nieelastyczny (mała wrażliwość na cenę)';
    if (elasticity > -1) return 'Jednostkowo elastyczny';
    if (elasticity > -2) return 'Elastyczny (średnia wrażliwość)';
    return 'Bardzo elastyczny (wysoka wrażliwość na cenę)';
  };

  const getPositionColor = (position: string) => {
    const colors: Record<string, string> = {
      'Bardzo konkurencyjna': 'text-green-400',
      'Konkurencyjna': 'text-blue-400',
      'Rynkowa': 'text-gray-400',
      'Premium': 'text-purple-400',
      'Luksusowa': 'text-yellow-400'
    };
    return colors[position] || 'text-gray-400';
  };

  return (
    <div className="p-6 space-y-6 bg-zinc-900 min-h-screen">
      <div>
        <h2 className="text-2xl font-bold mb-2 text-white">Optymalizator Cennika</h2>
        <p className="text-gray-400">
          Analizuj i optymalizuj ceny produktów dla maksymalizacji marży
        </p>
      </div>

      {/* Alert bezpieczeństwa */}
      <Alert className="border-blue-600/30 bg-blue-900/20">
        <Lock className="h-4 w-4 text-blue-400" />
        <AlertDescription className="text-gray-300">
          <strong className="text-blue-400">Moduł bezpieczny:</strong> Optymalizator tylko sugeruje ceny. 
          Żadne zmiany nie są wprowadzane automatycznie. Wszystkie modyfikacje wymagają ręcznego zatwierdzenia.
        </AlertDescription>
      </Alert>

      {message && (
        <Alert className={
          message.type === 'error' ? 'border-red-600/30 bg-red-900/20' : 
          message.type === 'warning' ? 'border-yellow-600/30 bg-yellow-900/20' :
          'border-green-600/30 bg-green-900/20'
        }>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={
            message.type === 'error' ? 'text-red-400' :
            message.type === 'warning' ? 'text-yellow-400' :
            'text-green-400'
          }>{message.text}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel konfiguracji */}
        <Card className="p-6 bg-zinc-800 border-zinc-700">
          <h3 className="text-lg font-semibold mb-4 text-white">Konfiguracja Optymalizacji</h3>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="product" className="text-gray-400">Wybierz produkt</Label>
              <select
                id="product"
                className="w-full mt-1 p-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                <option value="">-- Wybierz produkt --</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name} (obecna cena: {formatCurrency(product.currentPrice)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="targetMargin" className="text-gray-400">Docelowa marża (%)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="targetMargin"
                  type="number"
                  min="10"
                  max="80"
                  value={targetMargin}
                  onChange={(e) => setTargetMargin(Number(e.target.value))}
                  className="bg-zinc-700 border-zinc-600 text-white"
                />
                <span className="text-sm text-gray-400">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Zalecana marża: 35-45% dla standardowych produktów
              </p>
            </div>

            <Button
              onClick={runOptimization}
              disabled={!selectedProduct || loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analizowanie...
                </>
              ) : (
                <>
                  <Calculator className="w-4 h-4 mr-2" />
                  Uruchom Optymalizację
                </>
              )}
            </Button>
          </div>

          {/* Wskazówki */}
          <div className="mt-6 p-4 bg-blue-900/20 rounded-lg border border-blue-600/30">
            <h4 className="font-semibold text-blue-400 mb-2 flex items-center">
              <Info className="w-4 h-4 mr-2" />
              Wskazówki:
            </h4>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• Wyższa marża = większy zysk, ale potencjalnie mniejsza sprzedaż</li>
              <li>• Elastyczność cenowa pokazuje wrażliwość klientów na zmiany cen</li>
              <li>• Testuj zmiany stopniowo, obserwując reakcję rynku</li>
              <li>• Uwzględnij pozycję konkurencyjną przy ustalaniu cen</li>
              <li>• <span className="text-yellow-400">Wszystkie zmiany wymagają ręcznego zatwierdzenia</span></li>
            </ul>
          </div>
        </Card>

        {/* Panel wyników */}
        <Card className="p-6 bg-zinc-800 border-zinc-700">
          <h3 className="text-lg font-semibold mb-4 text-white">Wyniki Optymalizacji</h3>
          
          {optimization ? (
            <div className="space-y-4">
              <div className="p-4 bg-zinc-700/50 rounded-lg">
                <h4 className="font-semibold mb-2 text-white">{optimization.productName}</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Obecna cena</p>
                    <p className="text-xl font-bold text-white">{formatCurrency(optimization.currentPrice)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Sugerowana cena</p>
                    <p className="text-xl font-bold text-green-400">
                      {formatCurrency(optimization.suggestedPrice)}
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-zinc-600">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-400">Zmiana ceny</span>
                    <span className="font-semibold text-white">
                      {optimization.suggestedPrice > optimization.currentPrice ? '+' : ''}
                      {formatCurrency(optimization.suggestedPrice - optimization.currentPrice)}
                      {' '}
                      ({formatPercentage(((optimization.suggestedPrice - optimization.currentPrice) / optimization.currentPrice) * 100)})
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-400">Obecna marża</span>
                    <span className="font-semibold text-white">{formatPercentage(optimization.currentMargin)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-400">Docelowa marża</span>
                    <span className="font-semibold text-green-400">
                      {formatPercentage(optimization.targetMargin)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Wzrost marży</span>
                    <span className="font-semibold text-green-400">
                      +{formatPercentage(optimization.expectedIncrease)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Analiza elastyczności */}
              <div className="p-4 bg-yellow-900/20 rounded-lg border border-yellow-600/30">
                <div className="flex items-center mb-2">
                  <Activity className="w-4 h-4 mr-2 text-yellow-400" />
                  <h4 className="font-semibold text-yellow-400">Elastyczność Cenowa</h4>
                </div>
                <p className="text-sm text-gray-300">
                  Współczynnik: {optimization.elasticity.toFixed(2)}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {getElasticityInterpretation(optimization.elasticity)}
                </p>
              </div>

              {/* Pozycja konkurencyjna */}
              <div className="p-4 bg-purple-900/20 rounded-lg border border-purple-600/30">
                <div className="flex items-center mb-2">
                  <Target className="w-4 h-4 mr-2 text-purple-400" />
                  <h4 className="font-semibold text-purple-400">Pozycja Konkurencyjna</h4>
                </div>
                <p className={`text-lg font-semibold ${getPositionColor(optimization.position)}`}>
                  {optimization.position}
                </p>
              </div>

              {/* Potwierdzenie */}
              {confirmApply && (
                <div className="p-4 bg-orange-900/20 rounded-lg border border-orange-600/30">
                  <p className="text-orange-400 font-semibold mb-2">⚠️ Potwierdź zmianę ceny</p>
                  <p className="text-sm text-gray-300">
                    Czy na pewno chcesz zapisać sugerowaną cenę {formatCurrency(optimization.suggestedPrice)}?
                    Ta operacja zapisze sugestię, ale wymaga ręcznej aktualizacji w kalkulatorze.
                  </p>
                </div>
              )}

              {/* Przyciski akcji */}
              <div className="flex gap-2">
                <Button
                  onClick={applyOptimization}
                  disabled={saving}
                  className={`flex-1 ${confirmApply ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Zapisywanie...
                    </>
                  ) : confirmApply ? (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Potwierdź i Zapisz
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Zastosuj Sugestię
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => {
                    setOptimization(null);
                    setSelectedProduct('');
                    setConfirmApply(false);
                  }}
                  variant="secondary"
                  className="bg-zinc-700 hover:bg-zinc-600 text-white"
                >
                  Anuluj
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <DollarSign className="w-12 h-12 mb-4 text-gray-600" />
              <p className="text-gray-400">Wybierz produkt i uruchom optymalizację</p>
              <p className="text-sm mt-1 text-gray-500">aby zobaczyć rekomendacje cenowe</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};